/**
 * anok-scroll-reveal.js — Scroll-triggered animations for ANOK sections
 *
 * Level 1: Section fade-in (anok-reveal → anok-reveal--visible)
 * Level 2: Text reveal (anok-text-reveal → anok-text-reveal--visible)
 * Level 3: Stagger cascade (data-stagger-reveal → anok-stagger--visible)
 *
 * Mobile (≤749px): rootMargin reduced to 2/3 for earlier trigger.
 *
 * Opt-out: Liquid 側で対象外セクションに `.anok-no-reveal` を付与する
 * (anok-scroll-reveal.css の :not(.anok-no-reveal) と同期)。
 *
 * Reload 時 above/in viewport のセクションは即時表示 (アニメなし)。
 * このとき observer の first fire で reset がかかると不自然な退出アニメが
 * 出るため _revealSkipFirstReset / _staggerSkipFirstFire フラグで抑制する。
 * ユーザーがその後スクロールすれば通常通り entrance/exit アニメが効く。
 */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // reduced-motion 時も catch-all CSS を解除しないとセクションが永久に隠れる
    document.documentElement.classList.remove('anok-reveal-pending');
    return;
  }

  var isMobile = window.matchMedia('(max-width: 749px)').matches;

  // IntersectionObserver の threshold に 0 を指定すると一部ブラウザで発火しないため、実質ゼロの最小値を使用
  var INTERSECTION_THRESHOLD = 0.01;

  /* ============================================================
     Level 1: Section reveal + Level 2: Text reveal
     ============================================================ */

  function initSectionReveal() {
    var targets = []; // { section: HTMLElement, inner: HTMLElement }

    var shopifySections = document.querySelectorAll('.shopify-section');
    shopifySections.forEach(function (wrapper) {
      Array.from(wrapper.children).forEach(function (el) {
        if (el.nodeType !== 1) return;
        var tag = el.tagName;
        if (tag === 'LINK' || tag === 'STYLE' || tag === 'SCRIPT' || tag === 'NOSCRIPT') return;

        var hasAnokClass = Array.from(el.classList).some(function (cls) {
          return cls.startsWith('anok-');
        });
        if (!hasAnokClass) return;

        // Opt-out: .anok-no-reveal クラスがあれば対象外
        if (el.classList.contains('anok-no-reveal')) return;

        // inner ターゲット検出: 直接子 or __bg > __inner を探す
        var innerEl = el.querySelector(':scope > [class*="__inner"], :scope > * > [class*="__inner"]');
        var animTarget = innerEl || el; // フォールバック: __inner がなければ section 自体

        // data-reveal 属性から variant を取得（Liquid ハードコード分）。
        // `--no-transition` を同時に付与: .anok-reveal を追加すると default opacity:1
        // から CSS の opacity:0 への遷移が transition 発火（2s フェードアウト = 退出アニメ）
        // するため、init 中は transition を無効化する。2 フレーム後にまとめて剥がす。
        var revealVariant = el.getAttribute('data-reveal');
        if (revealVariant) {
          animTarget.classList.add('anok-reveal', 'anok-reveal--' + revealVariant, 'anok-reveal--no-transition');
        } else if (!animTarget.classList.contains('anok-reveal')) {
          animTarget.classList.add('anok-reveal', 'anok-reveal--no-transition');
        } else {
          animTarget.classList.add('anok-reveal--no-transition');
        }

        // section → inner のマッピングを保存
        el._revealTarget = animTarget;
        targets.push(el);
      });
    });

    if (targets.length === 0) {
      // 対象なしでも catch-all CSS は解除しないと html に pending class が残り続ける
      document.documentElement.classList.remove('anok-reveal-pending');
      return;
    }

    var revealMargin = isMobile ? '-100px' : '-300px';

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var animTarget = entry.target._revealTarget || entry.target;

          if (entry.isIntersecting) {
            // どこかで intersect 検知 → 「見られた」とみなして skip flag を消費
            entry.target._revealSkipFirstReset = false;

            animTarget.classList.add('anok-reveal--visible');

            var textElements = animTarget.querySelectorAll('.anok-text-reveal');
            textElements.forEach(function (el) {
              el.classList.add('anok-text-reveal--visible');
            });

            if (entry.target.getAttribute('data-reveal-repeat') === 'false') {
              observer.unobserve(entry.target);
            }
          } else {
            // First-fire skip: リロード時 above/in viewport で instant-visible にしたセクションは
            // observer の即時 fire で「center が下にあるから reset」とご判定されて退出アニメを起こす。
            // 1 回だけ skip して flag を消費する。
            if (entry.target._revealSkipFirstReset) {
              entry.target._revealSkipFirstReset = false;
              return;
            }

            // 上スクロールで要素が下に消えた → アニメーションリセット (退出アニメ)
            // rect.top > 0: セクションの上端がビューポート上端より下 (= 下方向に消えていく)。
            // 旧 centerY > innerHeight 条件はセクションサイズ依存で、短いセクションでは
            // reset されず --visible が剥がれない問題があった。
            var rect = entry.boundingClientRect;

            if (rect.top > 0) {
              animTarget.classList.remove('anok-reveal--visible');

              var textElements = animTarget.querySelectorAll('.anok-text-reveal');
              textElements.forEach(function (el) {
                el.classList.remove('anok-text-reveal--visible');
              });

              // セクション内のスタガーもリセット
              var staggerItems = entry.target.querySelectorAll('.anok-stagger--visible');
              staggerItems.forEach(function (item) {
                item.classList.remove('anok-stagger--visible');
              });
            }
          }
        });
      },
      {
        rootMargin: '0px 0px ' + revealMargin + ' 0px',
        threshold: INTERSECTION_THRESHOLD
      }
    );

    // 初回判定: ビューポート上 / 内にあるセクションは即時表示 (アニメなし)。
    // 全セクションを observe するが、即時表示した分は skip flag で first-reset を抑制する。
    // → スクロール後は通常の entrance/exit アニメが効く。
    // (--no-transition は上の add で全セクションに付与済み。下では --visible のみ追加)
    targets.forEach(function (el) {
      var animTarget = el._revealTarget;
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        el._revealSkipFirstReset = true;
        animTarget.classList.add('anok-reveal--visible');
        var textElements = animTarget.querySelectorAll('.anok-text-reveal');
        textElements.forEach(function (te) {
          te.classList.add('anok-text-reveal--visible');
        });
      }
      observer.observe(el);
    });

    // catch-all CSS を解除し、init 中に付与した --no-transition も次フレームで剥がす。
    // 全要素を querySelectorAll で取るため、stagger items が targets 外のセクション内にあっても OK。
    requestAnimationFrame(function () {
      document.documentElement.classList.remove('anok-reveal-pending');
      requestAnimationFrame(function () {
        document.querySelectorAll('.anok-reveal--no-transition').forEach(function (el) {
          el.classList.remove('anok-reveal--no-transition');
        });
        document.querySelectorAll('.anok-stagger--no-transition').forEach(function (el) {
          el.classList.remove('anok-stagger--no-transition');
        });
      });
    });
  }

  /* ============================================================
     Level 3: Stagger cascade
     ============================================================ */

  function initStagger() {
    var staggerContainers = document.querySelectorAll('[data-stagger-reveal]');
    if (staggerContainers.length === 0) return;

    // 初期クラス追加。
    // `--no-transition` を同時に付与: .anok-stagger-item を追加すると default opacity:1
    // から CSS の opacity:0 への遷移が transition 発火 (0.6s フェードアウト) するため。
    // 2 フレーム後にまとめて剥がす。
    staggerContainers.forEach(function (container) {
      var direction = container.getAttribute('data-stagger-reveal');
      var delay = parseFloat(container.getAttribute('data-stagger-delay') || '0.2');
      var children = Array.from(container.children);

      children.forEach(function (child, index) {
        child.classList.add('anok-stagger-item', 'anok-stagger-item--' + direction, 'anok-stagger--no-transition');
        child.style.transitionDelay = (index * delay) + 's';
      });
    });

    var defaultMargin = isMobile ? '-34px' : '-100px';

    var defaultStaggerOptions = {
      rootMargin: '0px 0px ' + defaultMargin + ' 0px',
      threshold: 0.01
    };

    var handleStaggerIntersection = function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          // First-fire skip: instant-visible の場合は再トリガー (visible→invisible→fade) を抑制
          if (entry.target._staggerSkipFirstFire) {
            entry.target._staggerSkipFirstFire = false;
            return;
          }

          var children = Array.from(entry.target.children);
          var delay = parseFloat(entry.target.getAttribute('data-stagger-delay') || '0.2');
          var direction = entry.target.getAttribute('data-stagger-reveal');

          // 表示時：slide-rightは末尾から、その他は先頭から
          children.forEach(function (child, index) {
            var finalIndex = direction === 'slide-right'
              ? children.length - 1 - index
              : index;
            child.style.transitionDelay = (finalIndex * delay) + 's';
          });

          // 再入時リセット
          var hasVisible = children.some(function (child) {
            return child.classList.contains('anok-stagger--visible');
          });

          if (hasVisible) {
            children.forEach(function (child) {
              child.classList.remove('anok-stagger--visible');
            });
            void entry.target.offsetHeight;
          }

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              children.forEach(function (child) {
                child.classList.add('anok-stagger--visible');
              });
            });
          });

          if (entry.target.getAttribute('data-stagger-repeat') === 'false') {
            obs.unobserve(entry.target);
          }
        } else {
          // First-fire skip (上スクロール側も同様)
          if (entry.target._staggerSkipFirstFire) {
            entry.target._staggerSkipFirstFire = false;
            return;
          }

          // 上スクロール時リセット (rect.top > 0 でサイズ非依存に判定)
          var rect = entry.boundingClientRect;

          if (rect.top > 0) {
            var children = Array.from(entry.target.children);
            var delay = parseFloat(entry.target.getAttribute('data-stagger-delay') || '0.2');
            var direction = entry.target.getAttribute('data-stagger-reveal');

            children.forEach(function (child, index) {
              var reverseIndex = children.length - 1 - index;
              var finalIndex = direction === 'slide-right'
                ? index
                : reverseIndex;
              child.style.transitionDelay = (finalIndex * delay) + 's';
            });

            requestAnimationFrame(function () {
              children.forEach(function (child) {
                child.classList.remove('anok-stagger--visible');
              });
            });
          }
        }
      });
    };

    var defaultStaggerObserver = new IntersectionObserver(handleStaggerIntersection, defaultStaggerOptions);

    staggerContainers.forEach(function (container) {
      // 初回判定: コンテナがビューポート上 / 内にあれば子要素を即時表示。skip flag で first-fire 抑制。
      // (--no-transition は上の add で全 stagger-item に付与済み。下では --visible のみ追加)
      var rect = container.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        container._staggerSkipFirstFire = true;
        var children = Array.from(container.children);
        children.forEach(function (child) {
          child.classList.add('anok-stagger--visible');
        });
      }

      var customRootMargin = container.getAttribute('data-stagger-rootmargin');
      var customThreshold = container.getAttribute('data-stagger-threshold');

      if (customRootMargin || customThreshold) {
        var effectiveMargin = customRootMargin || defaultStaggerOptions.rootMargin;
        if (isMobile && !customRootMargin) {
          effectiveMargin = defaultStaggerOptions.rootMargin;
        }
        var customOptions = {
          rootMargin: effectiveMargin,
          threshold: customThreshold ? parseFloat(customThreshold) : defaultStaggerOptions.threshold
        };
        var customObserver = new IntersectionObserver(handleStaggerIntersection, customOptions);
        customObserver.observe(container);
      } else {
        defaultStaggerObserver.observe(container);
      }
    });
  }

  /* ============================================================
     Init
     ============================================================ */

  function init() {
    initStagger();
    initSectionReveal();
  }

  // FOUC 抑制の catch-all (.anok-reveal-pending) は init 完了時の rAF でのみ外れる設計。
  // init が (将来のセクション追加・想定外の例外等で) L162 到達前に throw すると catch-all が
  // 残り、ページ全体が opacity:0 のまま復旧不能になる。多重の保険で必ず解除する。
  // classList.remove は冪等なので happy path では既に解除済みの no-op。
  function clearRevealPending() {
    document.documentElement.classList.remove('anok-reveal-pending');
  }

  function runInit() {
    try {
      init();
    } catch (e) {
      // 例外時も可視化を保証（catch-all が残るのを防ぐ）。原因は console に残す。
      clearRevealPending();
      throw e;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    runInit();
  }

  // バックストップ: init 経路が pending を外せなくても最終的に必ず解除する。
  // load は init (DOMContentLoaded/即時) の後に発火するため happy path では no-op。
  window.addEventListener('load', clearRevealPending);
  setTimeout(clearRevealPending, 3000);

  // Shopify theme editor support
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      var section = evt.target;
      // data-reveal セクションの inner にクラスを付与して即時表示
      var revealSections = section.querySelectorAll('[data-reveal]');
      revealSections.forEach(function (el) {
        var innerEl = el.querySelector(':scope > [class*="__inner"], :scope > * > [class*="__inner"]');
        var animTarget = innerEl || el;
        var variant = el.getAttribute('data-reveal');
        animTarget.classList.add('anok-reveal', 'anok-reveal--' + variant, 'anok-reveal--visible');
      });
      // 自動付与分も即時表示
      section.querySelectorAll('.anok-reveal').forEach(function (el) {
        el.classList.add('anok-reveal--visible');
      });
      section.querySelectorAll('.anok-stagger-item').forEach(function (el) {
        el.classList.add('anok-stagger--visible');
      });
    });
  }
})();
